import { useMemo } from "react";
import { t } from "@/i18n-js/instance";
import { FormSelectNative } from "@/react/components/shared/uikit/Form/FormSelectNative";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import { useIsPropertiesPanelRevampEnabled } from "@circle-react-shared/BuilderUI/Hooks/useIsPropertiesPanelRevamp";
import type { PropsMeta } from "@circle-react-shared/BuilderUI/PropsMeta.types";
import type { StyleMetaAttributes } from "@circle-react-shared/BuilderUI/PropsMetaStyle.types";
import { Form } from "@circle-react-uikit/Form";
import { useFormItem } from "./DefaultEditorV2/useFormItem";
import { AlignmentField } from "./fields/AlignmentField";
import { BorderRadiusSelector } from "./fields/BorderRadius";
import { BoxModelInput } from "./fields/BoxModelField";
import { ColorField } from "./fields/ColorField";
import { FormCustomHtmlEditor } from "./fields/CustomHTMLEditor/FormCustomHtmlEditor";
import { DimensionInput } from "./fields/DimensionInput";
import type { Unit } from "./fields/DimensionInput/constants";
import { EmbedField } from "./fields/EmbedField";
import { FontPicker } from "./fields/FontPicker";
import { FontWeight } from "./fields/FontWeight";
import { FormSelectField } from "./fields/FormSelectField";
import { HeadingLevelPicker } from "./fields/HeadingLevelPicker";
import { ImagePicker } from "./fields/ImagePicker";
import { ListField } from "./fields/ListField";
import { ParagraphLevelPicker } from "./fields/ParagraphLevelPicker";
import { RichTextField } from "./fields/RichTextField";
import { Sizing } from "./fields/Sizing";
import { SliderField } from "./fields/SliderField";
import { UrlField } from "./fields/UrlField";
import { VideoUploader } from "./fields/VideoUploader";
import { VisibilityField } from "./fields/VisibilityField";

export interface EditorFieldProps {
  name: string;
  propMeta: PropsMeta | StyleMetaAttributes;
}

export const DefaultEditorFields = ({ name, propMeta }: EditorFieldProps) => {
  const customEditorFields = useBuilderStore(state => state.customEditorFields);
  const isPropertiesPanelV2 = useIsPropertiesPanelRevampEnabled();
  const { getFormItemProps } = useFormItem();

  const label = useMemo(() => {
    const fallbackLabel = propMeta.name;
    const i18nKey = propMeta.nameI18n;
    return i18nKey
      ? t(i18nKey, { defaultValue: fallbackLabel })
      : fallbackLabel;
  }, [propMeta.name, propMeta.nameI18n]);

  const CustomFieldComponent = useMemo(() => {
    if (
      customEditorFields &&
      Object.hasOwn(customEditorFields, propMeta.type)
    ) {
      return customEditorFields[propMeta.type];
    }
    return null;
  }, [customEditorFields, propMeta.type]);

  if (CustomFieldComponent) {
    return <CustomFieldComponent name={name} propMeta={propMeta} />;
  }

  // Custom input fields should be handled by custom editor components.
  // This check here improves the type narrowing of the switch statement below.
  if (propMeta.prop === "custom") {
    return null;
  }

  switch (propMeta.type) {
    case "string":
      return (
        <Form.Item
          name={name}
          label={label}
          {...getFormItemProps({
            // eslint-disable-next-line i18next/no-literal-string -- False positive
            className: "!block",
            // eslint-disable-next-line i18next/no-literal-string -- False positive
            labelClassName: "!mb-1",
          })}
        >
          <Form.Input name={name} />
        </Form.Item>
      );

    case "textarea":
      return (
        <Form.Item
          name={name}
          label={label}
          {...getFormItemProps({
            // eslint-disable-next-line i18next/no-literal-string -- False positive
            className: "!block",
            // eslint-disable-next-line i18next/no-literal-string -- False positive
            labelClassName: "!mb-1",
          })}
        >
          <Form.Input name={name} variant="textarea" rows={7} />
        </Form.Item>
      );

    case "number":
      return (
        <Form.Item name={name} label={label} {...getFormItemProps()}>
          <Form.Input type="number" name={name} {...propMeta.numberProps} />
        </Form.Item>
      );

    case "boolean":
      return (
        <Form.Item
          name={name}
          label={label}
          shouldToggleValueOnLabelClick
          {...getFormItemProps({
            // eslint-disable-next-line i18next/no-literal-string -- False positive
            className: classNames("!items-center", {
              "!flex justify-between": !isPropertiesPanelV2,
              "!grid !mb-0": isPropertiesPanelV2,
            }),
          })}
        >
          <Form.ToggleSwitch
            name={name}
            variant="small"
            labelClassName={classNames({
              "mb-0": isPropertiesPanelV2,
            })}
          />
        </Form.Item>
      );

    case "circle-image-upload":
    case "background-image":
      return (
        <Form.Item
          name={name}
          label={label}
          hideDescription
          hideBorder
          labelClassName="!font-normal text-xs"
          className="!p-0"
        >
          <ImagePicker
            name={name}
            shouldAllowRemovingImage={!!propMeta.shouldAllowRemovingImage}
          />
        </Form.Item>
      );

    case "circle-video-upload":
      return <VideoUploader name={name} />;

    case "color":
    case "color-without-alpha":
      return (
        <ColorField
          name={name}
          label={label}
          shouldShowAlpha={propMeta.type === "color"}
          {...getFormItemProps()}
        />
      );

    case "select": {
      return (
        <Form.Item name={name} label={label} {...getFormItemProps()}>
          {/* 
            We can't use `FormTextSelect` due to limitations in the component
            to display the options above the input field.
          */}
          <FormSelectNative name={name} options={propMeta.options} />
        </Form.Item>
      );
    }

    case "custom-html":
      return (
        <Form.Item
          name={name}
          label={label}
          description={t("builder_ui.custom_html.disclaimer_message")}
          hideBorder
          labelClassName="!font-normal text-xs"
          className="!p-0"
        >
          {!isPropertiesPanelV2 && <div className="mt-2" />}
          <FormCustomHtmlEditor name={name} />
        </Form.Item>
      );

    case "list":
      return <ListField name={name} />;

    case "oembed":
      return <EmbedField name={name} label={label} />;

    case "slider": {
      if (isPropertiesPanelV2) {
        return (
          <DimensionInput
            name={name}
            label={label}
            units={propMeta.sliderProps.units as Unit[]}
          />
        );
      }
      return (
        <SliderField
          name={name}
          label={label}
          inputClassName="w-16"
          {...propMeta.sliderProps}
        />
      );
    }

    case "url":
      return <UrlField name={name} label={label} />;

    case "border-radius":
      return <BorderRadiusSelector name={name} />;

    case "alignment":
      return (
        <AlignmentField
          name={name}
          options={propMeta.alignment_options}
          label={label}
        />
      );

    case "dimension":
      return <DimensionInput name={name} label={label} />;

    case "sizing":
      return <Sizing />;

    case "box-model":
      return (
        <BoxModelInput
          name={name}
          label={label}
          type={propMeta.boxModelType ?? "padding"}
        />
      );

    case "heading-level-picker":
      return <HeadingLevelPicker label={label} />;

    case "rich-text":
      return <RichTextField name={name} propMeta={propMeta} />;
    case "paragraph-style-picker":
      return <ParagraphLevelPicker label={label} />;

    case "form-select":
      return <FormSelectField name={name} propMeta={propMeta} />;

    case "visibility":
      return <VisibilityField name={name} label={label} />;

    case "font-picker":
      return <FontPicker name={name} />;

    case "font-weight":
      return <FontWeight name={name} />;

    default:
      return null;
  }
};
